import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import type { GraphLayoutRequest, GraphPath, Layout } from '@ipfabric/utils';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import {
  type TopologyGraphMetadata,
  isTopologyGraphMetadata,
} from 'components/Graph/GraphState';
import { withGraphsLoadingIndicator } from 'components/Graph/commands/helpers/withGraphsLoadingIndicator';
import { performUndoableAction } from 'components/Graph/commands/history/performUndoableAction';
import { loadGraph } from 'components/Graph/commands/loadGraph';
import { savePathDefaultLayout } from 'components/Graph/commands/savePathDefaultLayout';
import {
  type NodeMovePosition,
  setNodePositions,
} from 'components/Graph/commands/setNodePositions';
import type {
  GraphCommandContext,
  GraphCommandFactory,
} from 'components/Graph/model/GraphCommandContext';
import { nodesDataFromIds } from 'components/Graph/model/GraphData';
import { selectNodesByPath } from 'components/Graph/model/selectors/selectNodesByPath';
import { option, readonlyArray } from 'fp-ts';
import { hasOwnProperty as recordHasOwnProperty } from 'fp-ts/lib/Record';
import { constVoid, pipe } from 'fp-ts/lib/function';
import type { CommandDispatcher } from 'utils/react/useCommandDispatcher';

type LayoutUpdateRequest = GraphLayoutRequest & { layout: Layout };

export interface LayoutPathCommand {
  layoutRequest: LayoutUpdateRequest;
}

const isWholePathLayoutRequest = (layoutRequest: LayoutUpdateRequest) =>
  layoutRequest.nodeSns === undefined;

type SaveDefaultLayoutFunction = (state: 'before' | 'after') => Awaitable<void>;

const makeSaveDefaultLayoutForWholePath = (
  cmdContext: GraphCommandContext,
  dispatch: CommandDispatcher<GraphCommandContext>,
  topologyMetadata: TopologyGraphMetadata,
  layoutRequest: LayoutUpdateRequest,
): SaveDefaultLayoutFunction => {
  if (!isWholePathLayoutRequest(layoutRequest)) {
    return constVoid;
  }

  if (!recordHasOwnProperty(layoutRequest.path, topologyMetadata.paths)) {
    stumble(`Current layout not available for path ${layoutRequest.path}`);
  }

  const layoutStates = {
    before:
      topologyMetadata.paths[layoutRequest.path]?.defaultLayout ??
      'userDefined',
    after: layoutRequest.layout,
  };

  return async (state: 'before' | 'after') => {
    await withGraphsLoadingIndicator(cmdContext)(() =>
      dispatch(savePathDefaultLayout, {
        groupBy: topologyMetadata.parameters.groupBy,
        layout: layoutStates[state],
        path: layoutRequest.path,
      }),
    );
    cmdContext.updateState((draft) => {
      draft.defaultPathLayouts[topologyMetadata.parameters.groupBy][
        layoutRequest.path
      ] = layoutStates[state];
    });
  };
};

const makeApplyLayoutingState =
  (
    cmdContext: GraphCommandContext,
    dispatch: CommandDispatcher<GraphCommandContext>,
    path: GraphPath,
    saveDefaultLayoutForWholePath: SaveDefaultLayoutFunction,
    nodePositions: {
      before: readonly NodeMovePosition[];
      after: readonly NodeMovePosition[];
    },
    nodesRepositioned: {
      before: boolean;
      after: boolean;
    },
  ) =>
  async (state: 'before' | 'after') => {
    await saveDefaultLayoutForWholePath(state);
    await dispatch(setNodePositions, {
      positions: nodePositions[state],
      undoable: false,
    });
    cmdContext.updateState((draft) => {
      if (!isTopologyGraphMetadata(draft.graphMetadata)) {
        stumble('Cannot update repositioned nodes');
        return;
      }
      if (nodesRepositioned[state]) {
        draft.graphMetadata.nodesRepositioned.add(path);
      } else {
        draft.graphMetadata.nodesRepositioned.delete(path);
      }
    });
  };

const getNodeMovePositions = (
  cmdContext: GraphCommandContext,
  path: GraphPath,
): readonly NodeMovePosition[] =>
  pipe(
    selectNodesByPath(cmdContext.getState()),
    plainMap.get(path),
    option.fold(() => [], plainSet.toArray),
    nodesDataFromIds(cmdContext.getState().graph.nodes),
    readonlyArray.map((nodeData) => ({
      nodeId: nodeData.id,
      moveTo: nodeData.position,
    })),
  );

export const layoutPath: GraphCommandFactory<LayoutPathCommand> =
  (cmdContext, dispatch) =>
  async ({ layoutRequest }) => {
    const topologyMetadata = cmdContext.getState().graphMetadata;
    if (!isTopologyGraphMetadata(topologyMetadata)) {
      stumble('Cannot perform layouting when not in topology mode');
      return;
    }
    const saveDefaultLayoutForWholePath = makeSaveDefaultLayoutForWholePath(
      cmdContext,
      dispatch,
      topologyMetadata,
      layoutRequest,
    );

    const nodePositionsBefore = getNodeMovePositions(
      cmdContext,
      layoutRequest.path,
    );
    const nodesRepositionedBefore = topologyMetadata.nodesRepositioned.has(
      layoutRequest.path,
    );
    await saveDefaultLayoutForWholePath('after');
    await dispatch(loadGraph, {
      overlay: 'keep',
      parameters: {
        ...topologyMetadata.parameters,
        layouts: [layoutRequest],
      },
      preserveHistory: true,
    });

    const applyLayoutingState = makeApplyLayoutingState(
      cmdContext,
      dispatch,
      layoutRequest.path,
      saveDefaultLayoutForWholePath,
      {
        before: nodePositionsBefore,
        after: getNodeMovePositions(cmdContext, layoutRequest.path),
      },
      {
        before: nodesRepositionedBefore,
        after: !isWholePathLayoutRequest(layoutRequest),
      },
    );

    dispatch(performUndoableAction, {
      skipInitialRedo: true,
      undo: () => applyLayoutingState('before'),
      redo: () => applyLayoutingState('after'),
    });
  };
